import type { PluginAction, PluginDataScope, PluginSurface } from "@scoach/types";
import { MARKETPLACE_CATEGORIES, PLUGIN_ACTIONS } from "@scoach/types";
import { Modal, useToast } from "@scoach/ui";
import { useState } from "react";

import { pluginsApi } from "../plugins/api.ts";

export function SuggestPluginModal({
  open,
  onClose,
  onSubmitted,
  initialEmbedUrl = "",
}: {
  open: boolean;
  onClose: () => void;
  onSubmitted: () => void;
  initialEmbedUrl?: string;
}) {
  const toast = useToast();
  const [name, setName] = useState("");
  const [embedUrl, setEmbedUrl] = useState(initialEmbedUrl);
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState<string>(MARKETPLACE_CATEGORIES[0]);
  const [surfaces, setSurfaces] = useState<PluginSurface[]>(["live"]);
  const [dataScope, setDataScope] = useState<PluginDataScope>("metadata");
  const [actions, setActions] = useState<PluginAction[]>([]);
  // Default to "private" — the low-friction path: use it yourself right now, no
  // approval. Publishing to everyone is an explicit opt-in (needs admin review).
  const [visibility, setVisibility] = useState<"private" | "public">("private");
  const [busy, setBusy] = useState(false);

  function toggle<T>(arr: T[], v: T, set: (a: T[]) => void) {
    set(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
  }

  async function submit() {
    if (!name.trim() || !embedUrl.trim()) {
      toast.push({ tone: "error", message: "Name and embed URL are required." });
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.set("name", name.trim());
      fd.set("embedUrl", embedUrl.trim());
      fd.set("description", description.trim());
      fd.set("category", category);
      fd.set("surfaces", surfaces.join(","));
      fd.set("dataScope", dataScope);
      fd.set("actions", actions.join(","));
      fd.set("visibility", visibility);
      const res = await pluginsApi.suggest(fd);
      toast.push({
        tone: res.reviewState === "changes-requested" ? "error" : "success",
        message:
          visibility === "private"
            ? "Added — it's live for you now. Publish to everyone later from My plugins."
            : res.reviewState === "changes-requested"
              ? "Submitted — security review found issues to fix (see My plugins)."
              : "Submitted for security review + admin approval.",
      });
      onSubmitted();
      onClose();
    } catch (err) {
      toast.push({ tone: "error", message: `Failed: ${(err as Error).message}` });
    } finally {
      setBusy(false);
    }
  }

  const input = { width: "100%", boxSizing: "border-box" as const };

  return (
    <Modal open={open} onClose={onClose} title="Add a plugin" width={560}>
      <div style={{ padding: 16, display: "grid", gap: 12 }}>
        <p style={{ margin: 0, color: "var(--text-3)", fontSize: 13 }}>
          Your plugin runs in a sandboxed iframe. Choose whether it's just for you (available
          instantly) or published to everyone (automated security review + admin approval first).
        </p>
        <div style={{ display: "grid", gap: 6 }}>
          <span style={{ fontSize: 12, fontWeight: 600 }}>Who can use it</span>
          <label style={{ fontSize: 13, display: "flex", gap: 6, alignItems: "flex-start" }}>
            <input
              type="radio"
              name="oa-visibility"
              checked={visibility === "private"}
              onChange={() => setVisibility("private")}
            />
            <span>
              <strong>Just for me</strong> — use it right now, no approval needed.
            </span>
          </label>
          <label style={{ fontSize: 13, display: "flex", gap: 6, alignItems: "flex-start" }}>
            <input
              type="radio"
              name="oa-visibility"
              checked={visibility === "public"}
              onChange={() => setVisibility("public")}
            />
            <span>
              <strong>Publish to everyone</strong> — goes through security review + admin approval.
            </span>
          </label>
        </div>
        <label style={{ display: "grid", gap: 4 }}>
          <span style={{ fontSize: 12, fontWeight: 600 }}>Name *</span>
          <input
            className="task-edit-input"
            style={input}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        <label style={{ display: "grid", gap: 4 }}>
          <span style={{ fontSize: 12, fontWeight: 600 }}>Embed URL * (https)</span>
          <input
            className="task-edit-input"
            style={input}
            value={embedUrl}
            placeholder="https://your-plugin.example.com/widget"
            onChange={(e) => setEmbedUrl(e.target.value)}
          />
        </label>
        <label style={{ display: "grid", gap: 4 }}>
          <span style={{ fontSize: 12, fontWeight: 600 }}>Description</span>
          <textarea
            className="task-edit-input"
            style={{ ...input, minHeight: 64, resize: "vertical" }}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </label>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <label style={{ display: "grid", gap: 4 }}>
            <span style={{ fontSize: 12, fontWeight: 600 }}>Category</span>
            <select
              className="task-edit-input"
              style={input}
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              {MARKETPLACE_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>
          <label style={{ display: "grid", gap: 4 }}>
            <span style={{ fontSize: 12, fontWeight: 600 }}>Requested data scope</span>
            <select
              className="task-edit-input"
              style={input}
              value={dataScope}
              onChange={(e) => setDataScope(e.target.value as PluginDataScope)}
            >
              <option value="metadata">Metadata only</option>
              <option value="full">Full (transcript + summary)</option>
            </select>
          </label>
        </div>
        <div>
          <span style={{ fontSize: 12, fontWeight: 600 }}>Surfaces</span>
          <div style={{ display: "flex", gap: 12, marginTop: 4 }}>
            {(["live", "summary"] as PluginSurface[]).map((s) => (
              <label
                key={s}
                style={{ fontSize: 13, display: "flex", gap: 4, alignItems: "center" }}
              >
                <input
                  type="checkbox"
                  checked={surfaces.includes(s)}
                  onChange={() => toggle(surfaces, s, setSurfaces)}
                />{" "}
                {s}
              </label>
            ))}
          </div>
        </div>
        <div>
          <span style={{ fontSize: 12, fontWeight: 600 }}>Requested actions</span>
          <div style={{ display: "flex", gap: 12, marginTop: 4, flexWrap: "wrap" }}>
            {PLUGIN_ACTIONS.map((a) => (
              <label
                key={a}
                style={{ fontSize: 13, display: "flex", gap: 4, alignItems: "center" }}
              >
                <input
                  type="checkbox"
                  checked={actions.includes(a)}
                  onChange={() => toggle(actions, a, setActions)}
                />{" "}
                {a}
              </label>
            ))}
          </div>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button type="button" className="pill-btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="pill-btn primary"
            onClick={() => void submit()}
            disabled={busy || !name.trim() || !embedUrl.trim()}
          >
            {busy ? "Saving…" : visibility === "private" ? "Add for me" : "Submit for review"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
